import { groupBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { UseFieldArrayRemove } from "react-hook-form";
import { FundBasketStrategy } from "creso-core/types";

export interface Collection {
  name: string;
  description?: string;
  type: "proposal" | "basket";
  id?: number;
  metadata?: { strategy?: FundBasketStrategy } & Record<string, unknown>;
}

export function getCollectionKey(collection: Collection): string {
  return `collection_${collection.id ?? collection.name}`;
}

export interface UseCollectionGroupsOptions<TRow> {
  /**
   * Current rows of the field array. Should be reactive (e.g. the result of
   * `useWatch`) so amount-change detection stays in sync with edits.
   */
  rows: TRow[];
  remove: UseFieldArrayRemove;
  /** Extract the collection (basket / proposal) a row belongs to, if any. */
  getCollection: (row: TRow) => Collection | null | undefined;
  /**
   * Stable per-row identifier used to diff a row's amount against the original
   * amount captured at import time (e.g. `scheme_isin`, `fund_scheme_id`).
   */
  getSchemeKey: (row: TRow) => string | number | null | undefined;
  /** Extract the amount field used for "Custom" detection. */
  getAmount: (row: TRow) => string;
  /**
   * Optional row filter used when a single field array holds multiple
   * sub-types that should be grouped independently (e.g. SIP vs LUMPSUM).
   */
  filter?: (row: TRow) => boolean;
}

export function useCollectionGroups<TRow extends { id: unknown }>({
  rows,
  remove,
  getCollection,
  getSchemeKey,
  getAmount,
  filter,
}: UseCollectionGroupsOptions<TRow>) {
  type RowWithIdx = TRow & { idx: number };

  const [customCollectionKeys, setCustomCollectionKeys] = useState<Set<string>>(
    new Set()
  );
  const [originalCollectionAmounts, setOriginalCollectionAmounts] = useState<
    Record<string, Record<string, string>>
  >({});

  const { collectionGroups, otherRows } = useMemo(() => {
    const items = rows.map(
      (r, idx) => ({ ...(r as object), idx }) as RowWithIdx
    );
    const candidates = filter ? items.filter((r) => filter(r as TRow)) : items;
    const grouped = groupBy(candidates, (r) => {
      const collection = getCollection(r as TRow);
      return collection ? getCollectionKey(collection) : "none";
    });

    const collectionGroups = Object.entries(grouped)
      .filter(([key]) => key !== "none")
      .map(([key, groupRows]) => ({
        key,
        schemes: groupRows,
        collection: getCollection(groupRows[0] as TRow) as Collection,
      }));

    const otherRows: RowWithIdx[] = grouped.none ?? [];
    return { collectionGroups, otherRows };
  }, [filter, getCollection, rows]);

  const handleRemoveCollection = useCallback(
    (key: string, groupRows: { idx: number }[]) => {
      const indices = [...groupRows].sort((a, b) => b.idx - a.idx);
      indices.forEach((r) => remove(r.idx));
      setCustomCollectionKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setOriginalCollectionAmounts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [remove]
  );

  const handleRemoveScheme = useCallback(
    (idx: number, collectionKey?: string) => {
      remove(idx);
      if (collectionKey) {
        setCustomCollectionKeys((prev) => new Set(prev).add(collectionKey));
      }
    },
    [remove]
  );

  const recordOriginalAmounts = useCallback(
    (
      collectionId: number,
      entries: { key: string | number; amount: string }[]
    ) => {
      const collectionKey = `collection_${collectionId}`;
      setOriginalCollectionAmounts((prev) => ({
        ...prev,
        [collectionKey]: Object.fromEntries(
          entries.map((e) => [String(e.key), e.amount])
        ),
      }));
    },
    []
  );

  const handleAmountChange = useCallback(
    (collectionKey: string, newValue: string, currentIdx: number) => {
      const originals = originalCollectionAmounts[collectionKey];
      if (!originals) {
        setCustomCollectionKeys((prev) => new Set(prev).add(collectionKey));
        return;
      }
      const currentRow = rows[currentIdx];
      if (currentRow == null) return;

      const thisKey = getSchemeKey(currentRow);

      const isCustom = rows.some((r) => {
        const collection = getCollection(r);
        const rowKey = collection ? getCollectionKey(collection) : null;
        if (rowKey !== collectionKey) return false;
        const sKey = getSchemeKey(r);
        const original = originals[String(sKey ?? "")];
        if (original === undefined) return false;
        const current = sKey === thisKey ? newValue : getAmount(r);
        return current !== original;
      });

      setCustomCollectionKeys((prev) => {
        const next = new Set(prev);
        if (isCustom) next.add(collectionKey);
        else next.delete(collectionKey);
        return next;
      });
    },
    [originalCollectionAmounts, rows, getSchemeKey, getAmount, getCollection]
  );

  const clearCustomKey = useCallback((collectionKey: string) => {
    setCustomCollectionKeys((prev) => {
      const next = new Set(prev);
      next.delete(collectionKey);
      return next;
    });
  }, []);

  const resetCollectionState = useCallback(() => {
    setCustomCollectionKeys(new Set());
    setOriginalCollectionAmounts({});
  }, []);

  return {
    collectionGroups,
    otherRows,
    customCollectionKeys,
    handleRemoveCollection,
    handleRemoveScheme,
    handleAmountChange,
    recordOriginalAmounts,
    clearCustomKey,
    resetCollectionState,
  };
}
